// UserSettingsModal.jsx

import React, { useEffect, useState } from 'react';

// Context
import { ThemeMode, useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';

// Firebase
import { signOut } from 'firebase/auth';

// Routing
import { useNavigate } from 'react-router-dom';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Services
import { deleteNode } from 'services/nodes/NodeWriteService';

// CSS
import '../switcher/popup/settings/SettingsModal.css';
import './UserSettingsModal.css';

// Icons
import Icon from 'svg/Icon';

// Components
import SettingsModalButton from '../switcher/popup/settings/button/SettingsModalButton';
import SettingsAccountDisplayPane from '../switcher/popup/settings/components/SettingsAccountDisplayPane';
import SettingsLoginPane from '../switcher/popup/settings/components/SettingsLoginPane';
import SettingsMyFeedbackPane from '../switcher/popup/settings/components/SettingsMyFeedbackPane';
import SettingsAiFeedPane from '../switcher/popup/settings/components/SettingsAiFeedPane';
import SettingsProfilePhotoPane from '../switcher/popup/settings/components/SettingsProfilePhotoPane';
import SettingsUserNamePane from '../switcher/popup/settings/components/SettingsUserNamePane';
import Confirm from 'components/confirm/Confirm';
import Modal from 'components/modal/Modal';
import { auth } from 'firebaseConfig';

const TAB_PROFILE_PHOTO = 'profile-photo';
const TAB_USER_NAME = 'user-name';
const TAB_ACCOUNT_DISPLAY = 'theme-account';
const TAB_AI_FEED = 'ai-feed';
const TAB_MY_FEEDBACK = 'my-feedback';
const TAB_LOGIN = 'login';

const TAB_LABELS = {
    [TAB_PROFILE_PHOTO]: 'Profile Photo',
    [TAB_USER_NAME]: 'User Name',
    [TAB_ACCOUNT_DISPLAY]: 'Display',
    [TAB_AI_FEED]: 'AI feed',
    [TAB_MY_FEEDBACK]: 'My Feedback',
    [TAB_LOGIN]: 'Login',
};

const USER_SETTINGS_TABS = new Set([
    TAB_PROFILE_PHOTO,
    TAB_USER_NAME,
    TAB_ACCOUNT_DISPLAY,
    TAB_AI_FEED,
    TAB_MY_FEEDBACK,
    TAB_LOGIN,
]);

function resolveInitialUserSettingsTab(requestedTab) {
    const normalizedTab = String(requestedTab || '').trim();

    if (USER_SETTINGS_TABS.has(normalizedTab)) {
        return normalizedTab;
    }

    return TAB_ACCOUNT_DISPLAY;
}

/**
 * UserSettingsModal
 *
 * Renders account-level settings panels for the signed-in user.
 *
 * @param {boolean} isOpen - Whether the modal is currently open.
 * @param {function} setOpen - Setter for modal visibility.
 * @param {string|null} initialTab - Optional tab key to activate when the modal opens.
 * @param {function} onClose - Callback fired after modal close.
 *
 * @returns {ReactNode} The rendered user settings modal.
 */
const UserSettingsModal = React.memo(({
    isOpen,
    setOpen,
    initialTab = null,
    onClose,
}) => {

    // ----- CONTEXT ----- //

    const {
        theme,
        themeMode,
        setThemeModeOverride,
        isUsingBaseThemeDefault,
    } = useTheme();

    const { user, setUser } = useUser();

    // ----- ROUTING ----- //

    const navigate = useNavigate();

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();

    // ----- STATE ----- //

    const [activeTab, setActiveTab] = useState(TAB_ACCOUNT_DISPLAY);
    const [deleteAccountConfirmOpen, setDeleteAccountConfirmOpen] = useState(false);
    const [isDeletingAccount, setIsDeletingAccount] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const isDarkMode = themeMode === ThemeMode.DARK;
    const activeTabLabel = TAB_LABELS[activeTab] || 'Settings';
    const userDisplayName = String(
        user?.data_profile?.name || user?.title || user?.displayName || user?.email || 'Account'
    ).trim();
    const modalTitle = `Settings / ${userDisplayName}`;

    // ----- EFFECTS ----- //

    useEffect(() => {
        if (!isOpen) return;

        setActiveTab(resolveInitialUserSettingsTab(initialTab));
    }, [initialTab, isOpen]);

    useEffect(() => {
        if (isMobile && isOpen) return;

        setIsMobileMenuOpen(false);
    }, [isMobile, isOpen]);

    // ----- HANDLERS ----- //

    /**
 * Handles close.
 */

    const handleClose = () => {
        setDeleteAccountConfirmOpen(false);
        setIsMobileMenuOpen(false);
        setOpen && setOpen(false);
        onClose && onClose();
    };

    /**
 * Handles tab change.
 *
 * @param {*} nextTab
 */
    const handleTabChange = (nextTab) => {
        setActiveTab(nextTab);
        setIsMobileMenuOpen(false);
    };

    /**
 * Handles user name tab click.
 */
    const handleUserNameTabClick = () => {
        handleTabChange(TAB_USER_NAME);
    };

    /**
 * Handles profile photo tab click.
 */
    const handleProfilePhotoTabClick = () => {
        handleTabChange(TAB_PROFILE_PHOTO);
    };

    /**
 * Handles account display tab click.
 */
    const handleAccountDisplayTabClick = () => {
        handleTabChange(TAB_ACCOUNT_DISPLAY);
    };

    /**
     * handleAiFeedTabClick
     *
     * Handles orbital ai feed tab click.
     */
    const handleAiFeedTabClick = () => {
        handleTabChange(TAB_AI_FEED);
    };

    /**
     * Handles my feedback tab click.
     */
    const handleMyFeedbackTabClick = () => {
        handleTabChange(TAB_MY_FEEDBACK);
    };

    /**
 * Handles login tab click.
 */
    const handleLoginTabClick = () => {
        handleTabChange(TAB_LOGIN);
    };

    /**
 * Handles account theme mode change.
 *
 * @param {*} nextEnabled
 */
    const handleAccountThemeModeChange = (nextEnabled) => {
        setThemeModeOverride(nextEnabled ? ThemeMode.DARK : ThemeMode.LIGHT);
    };

    /**
 * Signs the user out and returns to the login page.
 */
    const handleSignOut = async () => {
        await signOut(auth);
        setUser && setUser(null);
        handleClose();
        navigate('/');
    };

    /**
 * Handles delete account click.
 */
    const handleDeleteAccountClick = () => {
        setIsMobileMenuOpen(false);
        setDeleteAccountConfirmOpen(true);
    };

    /**
 * Handles mobile menu toggle.
 */
    const handleMobileMenuToggle = () => {
        setIsMobileMenuOpen((prev) => !prev);
    };

    /**
 * Handles confirm delete account.
 */
    const handleConfirmDeleteAccount = async () => {
        if (isDeletingAccount) return;

        const userKey = String(user?.key || '').trim();
        if (!userKey) {
            setDeleteAccountConfirmOpen(false);
            return;
        }

        setIsDeletingAccount(true);

        try {
            await deleteNode(userKey);
            setDeleteAccountConfirmOpen(false);
            setUser && setUser(null);
            handleClose();
            navigate('/', { replace: true });
        } catch (error) {
            console.error('[UserSettingsModal] Failed to delete account:', error);
            window.alert('Unable to delete account right now. Please try again.');
        } finally {
            setIsDeletingAccount(false);
        }
    };

    const navigationMainContents = (
        <>
            <div
              className="settings-modal-nav-group-label"
              style={{
                    color: theme.foregroundMuted,
                }}
            >
                MY ACCOUNT
            </div>

            <SettingsModalButton
              iconKey="person"
              label="My Name"
              selected={activeTab === TAB_USER_NAME}
              onClick={handleUserNameTabClick}
            />

            <SettingsModalButton
              iconKey="account_circle"
              label="Profile Photo"
              selected={activeTab === TAB_PROFILE_PHOTO}
              onClick={handleProfilePhotoTabClick}
            />

            <SettingsModalButton
              iconKey="display_settings"
              label="Display"
              selected={activeTab === TAB_ACCOUNT_DISPLAY}
              onClick={handleAccountDisplayTabClick}
            />

            <SettingsModalButton
              iconKey="wand_stars"
              label="Appdeck AI"
              selected={activeTab === TAB_AI_FEED}
              onClick={handleAiFeedTabClick}
            />

            <SettingsModalButton
              iconKey="thumb_up"
              label="My Feedback"
              selected={activeTab === TAB_MY_FEEDBACK}
              onClick={handleMyFeedbackTabClick}
            />

            <SettingsModalButton
              iconKey="lock"
              label="Login Info"
              selected={activeTab === TAB_LOGIN}
              onClick={handleLoginTabClick}
            />

            <SettingsModalButton
              iconKey="logout"
              label="Sign Out"
              onClick={handleSignOut}
            />
        </>
    );

    const navigationFooterContents = (
        <SettingsModalButton
          iconKey="delete"
          label="Delete Account"
          onClick={handleDeleteAccountClick}
        />
    );

    const navigationContents = (
        <>
            {/* NAV MAIN */}
            <div className="settings-modal-nav-main">
                {navigationMainContents}
            </div>

            {/* NAV FOOTER */}
            <div
              className="settings-modal-nav-footer"
            >
                {navigationFooterContents}
            </div>
        </>
    );

    // ----- RENDER ----- //

    return (
        <>
            <Modal
              iconKey="person"
              title={modalTitle}
              isOpen={isOpen}
              height={isMobile ? '100%' : '760px'}
              width={isMobile ? '100%' : '920px'}
              onClose={handleClose}
            >
                <div className="settings-modal-container">

                    {!isMobile && (
                        <div
                          className="settings-modal-nav"
                          style={{
                                borderRightColor: theme.backgroundHover,
                            }}
                        >
                            {navigationContents}
                        </div>
                    )}

                    <div
                      className="settings-modal-pane"
                      style={{
                            margin: isMobile ? '12px' : '0px',
                        }}
                    >
                        {isMobile && (
                            <div className="settings-modal-mobile-menu">
                                <button
                                  className="settings-modal-mobile-menu-button"
                                  style={{
                                        backgroundColor: isMobileMenuOpen ? theme.backgroundHover : theme.backgroundMuted,
                                        borderColor: theme.backgroundHover,
                                    }}
                                  type="button"
                                  aria-expanded={isMobileMenuOpen}
                                  aria-label="Open account settings menu"
                                  onClick={handleMobileMenuToggle}
                                >
                                    <div className="settings-modal-mobile-menu-button-main">
                                        <div className="settings-modal-mobile-menu-button-icon">
                                            <Icon
                                              iconKey="menu"
                                              width={18}
                                              height={18}
                                              color={theme.foregroundMuted}
                                            />
                                        </div>

                                        <div
                                          className="settings-modal-mobile-menu-button-title"
                                          style={{
                                                color: theme.foreground,
                                            }}
                                        >
                                            Menu
                                        </div>
                                    </div>

                                    <div className="settings-modal-mobile-menu-button-main">
                                        <div
                                          className="settings-modal-mobile-menu-button-value"
                                          style={{
                                                color: theme.foregroundMuted,
                                            }}
                                        >
                                            {activeTabLabel}
                                        </div>

                                        <div className="settings-modal-mobile-menu-button-icon">
                                            <Icon
                                              iconKey={isMobileMenuOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                                              width={18}
                                              height={18}
                                              color={theme.foregroundMuted}
                                            />
                                        </div>
                                    </div>
                                </button>

                                {isMobileMenuOpen && (
                                    <>
                                        <div
                                          className="settings-modal-mobile-menu-backdrop"
                                          onClick={() => setIsMobileMenuOpen(false)}
                                        />

                                        <div
                                          className="settings-modal-mobile-menu-popup"
                                          style={{
                                                backgroundColor: theme.background,
                                                borderColor: theme.backgroundHover,
                                            }}
                                        >
                                            <div className="settings-modal-mobile-menu-popup-nav">
                                                {navigationContents}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {activeTab === TAB_ACCOUNT_DISPLAY && (
                            <SettingsAccountDisplayPane
                              theme={theme}
                              isDarkMode={isDarkMode}
                              isUsingBaseDefault={isUsingBaseThemeDefault}
                              onThemeModeChange={handleAccountThemeModeChange}
                            />
                        )}

                        {activeTab === TAB_AI_FEED && (
                            <SettingsAiFeedPane />
                        )}

                        {activeTab === TAB_MY_FEEDBACK && (
                            <SettingsMyFeedbackPane theme={theme} />
                        )}

                        {activeTab === TAB_PROFILE_PHOTO && (
                            <SettingsProfilePhotoPane
                              theme={theme}
                              user={user}
                            />
                        )}

                        {activeTab === TAB_USER_NAME && (
                            <SettingsUserNamePane
                              theme={theme}
                              user={user}
                            />
                        )}

                        {activeTab === TAB_LOGIN && (
                            <SettingsLoginPane />
                        )}
                    </div>

                </div>
            </Modal>

            <Confirm
              title="Delete Account"
              message="Delete your account and all associated data? This action cannot be undone."
              isOpen={deleteAccountConfirmOpen}
              setOpen={setDeleteAccountConfirmOpen}
              onConfirm={handleConfirmDeleteAccount}
            />
        </>
    );

});

// Dev Tools
UserSettingsModal.displayName = 'UserSettingsModal';

export default UserSettingsModal;
